import React, { Fragment, useEffect, useState } from 'react'
import { Dialog } from './ui/dialog'
import { Icon, Spinner, Classes } from '@blueprintjs/core'
import { HTMLSelect } from './ui/html-select'
import classNames from 'classnames'
import { percentage } from '../util/formatters'
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '../../../tailwind.config'
import { axiosGet, axiosPost, axiosPatch } from '../util/api'
import { fetchSmartBidSettings, fetchSmartBidSettingsIfNeeded } from '../actions/smart_bid_settings'
import { useDispatch, useSelector } from 'react-redux'
import keyBy from 'lodash.keyby'
import { EventParentTypes } from '../config'
import { Toasts } from '../packages/blueprint-components/src/Toaster'
import { createSelector } from 'reselect'
import { SmartBidSettingForm } from './smart_bid_settings/SmartBidSettingsForm'
import { Tooltip } from './ui/tooltip'
import { Button } from './ui/button'

const fullConfig = resolveConfig(tailwindConfig)

const Inherited = -123
const NewSmartBidSetting = -1

const smartBidSettingsSelector = createSelector(
    (store) => store.smartBidSettings.loading,
    (store) => store.smartBidSettings.smartBidSettings,
    (loading, list) => {
        return {
            loading,
            smartBidSettingsList: keyBy(list, 'id'),
        }
    },
)

export const SmartBidSettingBadge = ({
    active,
    className,
    parent_id,
    parent_type,
    onSave = undefined,
    mutateItems,
}) => {
    const dispatch = useDispatch()
    const color = active ? fullConfig.theme.colors.blue[600] : fullConfig.theme.colors.gray[400]
    const [open, setOpen] = useState(false)
    const [createdSmartBidSettings, setCreatedSmartBidSettings] = useState({})
    const { smartBidSettingsList, loading } = useSelector(smartBidSettingsSelector)
    const [selected, setSelected] = useState(active || Inherited)
    const [inheritedLoading, setInheritedLoading] = useState(true)
    const [inherited, setInherited] = useState(null)
    const [saving, setSaving] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    const smartBidSettings = {
        [Inherited]: inherited,
        ...smartBidSettingsList,
    }
    const smartBidSettingOptions = Object.values(smartBidSettingsList).map((sbs) => ({
        label: sbs.name,
        value: sbs.id,
    }))

    useEffect(() => {
        dispatch(fetchSmartBidSettingsIfNeeded())
    }, [])

    const save = () => {
        setSaving(true)
        setIsEditing(false)

        let payload = {
            parent_id,
            parent_type,
        }
        const isCreating = selected === NewSmartBidSetting
        if (isCreating || !!isEditing) {
            payload = {
                ...createdSmartBidSettings,
                ...payload,
            }
        } else {
            payload = {
                ...payload,
                smart_bid_setting_id: selected,
            }
        }

        let primaryKey = null
        switch (parent_type) {
            case EventParentTypes.KEYWORD:
                primaryKey = 'keyword_id'
                break
            case EventParentTypes.TARGETS:
                primaryKey = 'target_id'
                break
            case EventParentTypes.AD_GROUP:
                primaryKey = 'ad_group_id'
                break
            case EventParentTypes.CAMPAIGN:
                primaryKey = 'campaign_id'
                break
            case EventParentTypes.Profile:
                primaryKey = 'profile_id'
                break
            default:
                setSaving(false)
                throw new Error('Unhandled parent type: ' + parent_id)
        }

        if (!!isEditing) {
            axiosPatch(`smart-bid-settings/${isEditing}`, payload)
                .then((res) => {
                    setSaving(false)
                    Toasts.success('Smart Bidding settings successfully edited')
                    setOpen(false)
                    onSave ? onSave() : null
                    setSelected(res.data.id || Inherited)

                    mutateItems((items) =>
                        items.map((i) => {
                            if (i[primaryKey] === parent_id) {
                                return {
                                    ...i,
                                    smart_bid_setting: [res.data],
                                }
                            }
                            return i
                        }),
                    )

                    if (!!isEditing) {
                        dispatch(fetchSmartBidSettings())
                    }
                })
                .catch((res) => {
                    setSaving(false)
                })
        } else {
            axiosPost('smart-bid-settings', payload)
                .then((res) => {
                    setSaving(false)
                    Toasts.success('Smart Bidding settings saved')
                    setOpen(false)
                    onSave ? onSave() : null
                    setSelected(res.data.id || Inherited)

                    mutateItems((items) =>
                        items.map((i) => {
                            if (i[primaryKey] === parent_id) {
                                return {
                                    ...i,
                                    smart_bid_setting: [res.data],
                                }
                            }
                            return i
                        }),
                    )

                    if (isCreating) {
                        dispatch(fetchSmartBidSettings())
                    }
                })
                .catch((res) => {
                    setSaving(false)
                })
        }
    }

    const load = () => {
        dispatch(fetchSmartBidSettingsIfNeeded())

        setInheritedLoading(true)
        axiosGet('smart-bid-settings/inherited', {
            parent_id,
            parent_type,
        })
            .then((res) => {
                setInherited(res.data)
                setInheritedLoading(false)
            })
            .catch((res) => {
                setInheritedLoading(false)
            })
    }

    const isLoading = inheritedLoading || loading
    const onCancel = () => {
        setOpen(false)
        setIsEditing(false)
        setSelected(active ? active : Inherited)
    }
    return (
        <Fragment>
            <Tooltip
                className={classNames(className)}
                interactionKind="hover"
                hoverCloseDelay={150}
                onOpened={load}
                onClosed={() => setInheritedLoading(true)}
                content={
                    <div className="p-2">
                        {isLoading && <Spinner size={45} />}
                        {!isLoading && (
                            <Fragment>
                                <SmartBidSettingPreview
                                    sbs={smartBidSettings[selected]}
                                    inherited={selected === Inherited}
                                />

                                <Button
                                    text="Edit"
                                    className="mt-3"
                                    onClick={() => setOpen(true)}
                                />
                            </Fragment>
                        )}
                    </div>
                }
            >
                <Icon title={null} icon="predictive-analysis" size={20} color={color} />
            </Tooltip>
            <Dialog isOpen={open} className="p-3">
                <div className={Classes.DIALOG_BODY}>
                    <HTMLSelect
                        className="mb-3"
                        options={[
                            { value: Inherited, label: 'Inherit' },
                            { value: NewSmartBidSetting, label: 'Create New' },
                            ...smartBidSettingOptions,
                        ]}
                        value={selected}
                        onChange={(e) => {
                            const v = parseInt(e.target.value)
                            setIsEditing(false)
                            setSelected(v)
                        }}
                    />
                    {selected !== NewSmartBidSetting && !isEditing && (
                        <SmartBidSettingPreview
                            className="mb-3"
                            sbs={smartBidSettings[selected]}
                            showInherited={selected === Inherited}
                        />
                    )}
                    {selected === NewSmartBidSetting && (
                        <SmartBidSettingForm
                            className="mb-3"
                            loading={saving}
                            defaultSbs={smartBidSettings[Inherited]}
                            onChange={setCreatedSmartBidSettings}
                        />
                    )}
                    {!!isEditing && (
                        <SmartBidSettingForm
                            className="mb-3"
                            loading={saving}
                            defaultSbs={smartBidSettings[selected]}
                            onChange={setCreatedSmartBidSettings}
                        />
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={`d-flex justify-between`}>
                        <div>
                            <Button disabled={saving} text="Cancel" onClick={onCancel} />
                        </div>
                        <div>
                            {selected !== Inherited &&
                                selected !== NewSmartBidSetting &&
                                selected != isEditing && (
                                    <Button
                                        className="m-2"
                                        loading={saving}
                                        text="Edit"
                                        onClick={() => {
                                            setIsEditing(selected)
                                        }}
                                    />
                                )}
                            <Button loading={saving} text="Save" intent="primary" onClick={save} />
                        </div>
                    </div>
                </div>
            </Dialog>
        </Fragment>
    )
}

const SmartBidSettingPreview = ({ sbs, className, inherited = false }) => {
    return (
        <div className={classNames(className)}>
            <div>
                {inherited ? 'Inherited' : 'Using'} Smart Bidding settings{' '}
                {inherited ? '' : sbs.name}
                <br />
            </div>
            <div>
                Target ACoS Threshold: {percentage(sbs.target_acos_threshold / 100)}
                <br />
                Maximum Bid Adjustment: {percentage(sbs.maximum_bid_adjustment / 100)}
                <br />
                Lookback Days: {sbs.lookback_days}
                <br />
            </div>
        </div>
    )
}
